import { track } from '@/lib/analytics/track';
import { TrackingEventName, type TrackingEventV2 } from '@/models/analytics/eventTypes';
import { logger } from '@/utils/logger/logger';
import {
  type PatientSurfaceAnalyticsEvent,
  type PatientSurfaceEventName,
} from '@heidi/patients/contracts';

/**
 * The app-side emit host for the patient surface (`@heidi/patients`), and the
 * binding that makes the narrowed seam work (openspec task 2.1b, obstacle (a)).
 *
 * Ruling 24: the surface decides WHEN each event fires — the once-per-open
 * `view_id` lifecycle, the accordion expand, the upload completion, the
 * non-surface-consumer guard on the shared upload mutation — and supplies the
 * payload; this module adds the destination routing and the app union, and
 * nothing else.
 *
 * WHAT MOVED HERE AND WHAT DID NOT. This is
 * `src/features/patient-profile/analytics/emit.ts` verbatim in behaviour: same
 * `track()` path, same `{ amplitude: false }`, same never-throw contract. That
 * path is now a re-export shim, because four root modules and five suites name
 * it — including `jest.mock('./emit')` and a jest.mock of the old analytics
 * barrel, which a rename would break silently in the direction of a passing
 * test.
 *
 * THE NARROWING IS THE POINT. The old signature was
 * `(event: TrackingEventV2) => void` — the app's entire event union, so a
 * patient-surface emit accepted `session_summary_copied` and typechecked. That
 * signature also could not follow the domain into a package: `/contracts` is
 * inert and may not import `@/models/analytics/eventTypes`, so there was nothing
 * to re-export. Both problems have one fix, which is why the seam is narrowed
 * rather than relocated: `PatientSurfaceAnalyticsEvent` is a nine-member
 * discriminated union over string LITERALS, declared in
 * `@heidi/patients/contracts`, and this module is the only place that knows it
 * corresponds to `TrackingEventV2`.
 *
 * WHY THE CALL SITES READ `descriptor.name` AND THIS FILE STILL HOLDS THE ENUM.
 * The three landed 2.1 domains all had to keep `TrackingEventName.X` at the emit
 * site, because `event_name` discriminates `TrackingEventV2` on string enum
 * MEMBERS and a string literal does not compile in one's place. That constraint
 * applies to the boundary crossing, not to the whole path — so it lives here,
 * once, as the token map below, and the nine call sites upstream of it name the
 * event by reading `.name` off its single declaration site
 * (`@heidi/patients/contributions`), which is what D9 ruling 27 asks for and
 * what the enum-typed union previously made impossible.
 */

/**
 * The token map that binds patients' nine literal names to the app enum.
 *
 * TOTAL BY CONSTRUCTION, and that is the whole reason it is a map rather than a
 * cast: `satisfies Record<PatientSurfaceEventName, TrackingEventName>` requires
 * an entry for every member of the union, so widening
 * `PatientSurfaceAnalyticsEvent` without a `TrackingEventName` member to bind it
 * to fails HERE — at the binding, in this PR — rather than at the first emit in
 * production. The sibling `trackPatientSurfaceAnalytics.test.ts` asserts the
 * other direction, in `'$name is bound to the matching TrackingEventName
 * member'`: that each entry's runtime string equals the descriptor's frozen
 * `name`, so the enum is a binding rather than a second spelling. `satisfies`
 * alone cannot make that claim — every entry below is a valid
 * `TrackingEventName`, so a map pointing a token at the WRONG member still
 * satisfies the constraint, and only the string comparison catches it.
 */
const PATIENT_SURFACE_EVENT_NAMES = {
  patient_list_viewed: TrackingEventName.PatientListViewed,
  patient_profile_opened: TrackingEventName.PatientProfileOpened,
  patient_profile_tab_viewed: TrackingEventName.PatientProfileTabViewed,
  patient_session_expanded: TrackingEventName.PatientSessionExpanded,
  patient_field_edited: TrackingEventName.PatientFieldEdited,
  patient_document_uploaded: TrackingEventName.PatientDocumentUploaded,
  patient_document_linked: TrackingEventName.PatientDocumentLinked,
  patient_document_unlinked: TrackingEventName.PatientDocumentUnlinked,
  patient_recap_popover_opened: TrackingEventName.PatientRecapPopoverOpened,
} satisfies Record<PatientSurfaceEventName, TrackingEventName>;

/**
 * Fire-and-forget emit for patient-surface Snowplow events.
 *
 * - Calls `track()` directly (no React-redux hook, no dispatch), so it can be
 *   used from effects, event handlers, and mutation callbacks alike.
 * - Stamps `options: { amplitude: false }` — these events route to Snowplow
 *   only, per the analytics routing policy. ROUTING IS PRESERVED, NOT TIDIED:
 *   the pre-narrowing helper passed exactly this and none of the nine names is
 *   on the Snowplow denylist (`@/lib/analytics/snowplow-suppression`), so no
 *   `snowplow: false` is passed and none should be added here.
 * - Never throws: instrumentation must not crash product code, so any failure
 *   in the analytics pipeline is logged and swallowed. The `EmitPatientSurfaceEvent`
 *   port type in `@heidi/patients/contracts` states that contract, which is why
 *   no caller writes a `try`.
 */
export const emitPatientSurfaceEvent = (event: PatientSurfaceAnalyticsEvent): void => {
  try {
    track({
      // THE ONE CAST, and what makes it sound. `event` has already been checked
      // as a discriminated union, so its name/props pairing is proven at the
      // call site; the map then swaps the literal name for the enum member with
      // the identical runtime value. What TypeScript cannot follow is that the
      // swap preserves the pairing — it re-checks `TrackingEventV2` against the
      // now-widened `event_name` and sees nine possible props for each name.
      // Asserting the composed object is narrower than casting either field
      // alone, and the pairing it stands in for is pinned by the union in
      // `/contracts` plus this module's suite.
      ...({
        event_name: PATIENT_SURFACE_EVENT_NAMES[event.event_name],
        props: event.props,
      } as TrackingEventV2),
      options: { amplitude: false },
    });
  } catch (error) {
    logger.warn('[patient-surface] analytics emit failed', error);
  }
};
